import React, { useState, useEffect, useRef } from "react";

/* FROZEN — end of Phase: Shared-Surface Discovery (Feb 2026).
   Study 2 (corrective contribution) is preserved as a stable
   artifact of this phase. Do not modify. If future studies
   contradict its behavior, create a new route rather than
   editing this one. */

/**
 * Vessel — Correction-path Physics Study.
 *
 * The second physics study. Tests whether a Vessel can behave as a
 * genuinely shared thinking surface when SONMI and the steward do
 * not immediately agree.
 *
 * Simone is a subject at the edge of the environment, not the source
 * of the Vessel. The Vessel forms in the shared surface because both
 * parties need a temporary place to think about her together. The
 * trace remaining after dissolution belongs to the shared surface,
 * not to Simone.
 *
 * Corrections in this study are not edits, deletions, tracked
 * changes, or accepted suggestions. When the steward disagrees:
 *   - the valid part of SONMI's contribution stays anchored,
 *   - the incorrect part loosens (opacity drops, drifts, returns to
 *     the environment),
 *   - the steward's contribution anchors on its own weight,
 *   - the surviving contributions reorganize toward each other,
 *   - a revised understanding neither began with materializes below.
 *
 * Voice: both parties use Outfit. Authorship is preserved through
 * the smallest possible cue — a 1.5px vertical mark to the left of
 * each contribution (amber for SONMI, stone for the steward). The
 * revised understanding carries no mark; it belongs to both.
 */

const STEP = {
  ENV: 0,           // environment still, invitation visible
  SONMI_ARRIVE: 1,  // SONMI's contribution gathering
  STEWARD_TURN: 2,  // SONMI present, steward's cursor invites typing
  STEWARD_LANDED: 3,// steward has submitted; SONMI's wrong clause registers the disagreement (subtle dip)
  REORG: 4,         // wrong clause loosens fully; survivors draw together
  SYNTH: 5,         // revised understanding materializes
  DISSOLVE: 6,      // Vessel fades; drop arrives in the surface
  AFTER: 7,         // return to environment; drop persists
};

export default function VesselCorrection() {
  const [step, setStep] = useState(STEP.ENV);
  const [stewardText, setStewardText] = useState("");
  const editRef = useRef(null);
  const timerRef = useRef(null);

  // Ghost text — placeholder invitation, not a script. Steward may type anything.
  const GHOST = "Not quite. She isn't uncertain — she's checking how the change will affect everyone else.";

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (step === STEP.SONMI_ARRIVE) {
      timerRef.current = setTimeout(() => setStep(STEP.STEWARD_TURN), 1500);
    } else if (step === STEP.STEWARD_TURN) {
      // Focus the editable so the cursor is visible without a click.
      setTimeout(() => editRef.current && editRef.current.focus(), 100);
    } else if (step === STEP.STEWARD_LANDED) {
      timerRef.current = setTimeout(() => setStep(STEP.REORG), 900);
    } else if (step === STEP.REORG) {
      timerRef.current = setTimeout(() => setStep(STEP.SYNTH), 1300);
    } else if (step === STEP.DISSOLVE) {
      timerRef.current = setTimeout(() => setStep(STEP.AFTER), 1300);
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [step]);

  const submitSteward = () => {
    if (step !== STEP.STEWARD_TURN) return;
    // If the steward hasn't typed anything, use the ghost as a
    // stand-in so the study still runs; but they truly could have
    // typed anything.
    if (!stewardText.trim()) setStewardText(GHOST);
    setStep(STEP.STEWARD_LANDED);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitSteward();
    }
  };

  const reset = () => {
    setStewardText("");
    if (editRef.current) editRef.current.textContent = "";
    setStep(STEP.ENV);
  };

  // Convenience booleans for readability
  const vesselActive = step >= STEP.SONMI_ARRIVE && step <= STEP.SYNTH;
  const wrongClauseLoosening = step === STEP.STEWARD_LANDED;
  const wrongClauseGone = step >= STEP.REORG;
  const survivorsDrawing = step >= STEP.REORG && step <= STEP.SYNTH;

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif" }}
      data-testid="physics-correction-root"
    >
      {/* Study label — same corner as study 1, deliberately minimal. */}
      <div
        className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400"
        data-testid="study-label"
      >
        SONMI · physics study · correction path · placeholder content
      </div>

      {/* Reset — corner, not bottom-center. Only visible after dissolution. */}
      {step === STEP.AFTER && (
        <button
          onClick={reset}
          data-testid="reset"
          className="absolute top-6 right-8 text-[11px] italic text-stone-400 hover:text-stone-700 transition-colors"
        >
          watch again
        </button>
      )}

      {/* Simone — a reference at the edge, deliberately peripheral.
          Smaller than study 1. Unchanged across all steps. No drop
          attaches to her. */}
      <div
        className="absolute"
        style={{ left: "9%", top: "50%", transform: "translateY(-50%)" }}
        data-testid="subject-simone"
      >
        <div className="flex flex-col items-center">
          <div
            className="rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500"
            style={{
              width: 48,
              height: 48,
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              fontSize: 18,
            }}
          >
            SC
          </div>
          <div
            className="mt-2 text-[12px] text-stone-500"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
          >
            Simone
          </div>
        </div>
      </div>

      {/* Central invitation — visible only in the still environment.
          Contextual (near where the Vessel will form), not a bottom
          control. */}
      {step === STEP.ENV && (
        <button
          onClick={() => setStep(STEP.SONMI_ARRIVE)}
          data-testid="invitation"
          className="absolute text-[13px] italic text-stone-400 hover:text-stone-700 transition-colors"
          style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
        >
          sit here with something about simone
        </button>
      )}

      {/* The Vessel — no coloured region, no perimeter. It is legible
          only through the proximity and behaviour of its contents. */}
      {vesselActive && (
        <div
          className="absolute"
          style={{ left: "32%", top: "18%", right: "12%", bottom: "18%" }}
          data-testid="vessel-contents"
        >
          {/* SONMI's opening — TWO clauses so one can loosen and one can stay. */}
          <div
            className="absolute transition-all duration-1200"
            style={{
              top: survivorsDrawing ? "8%" : "6%",
              left: 0,
              right: "8%",
              opacity: step === STEP.SONMI_ARRIVE ? 0.35 : 1,
              transform:
                step === STEP.SONMI_ARRIVE ? "translateY(10px)" : "translateY(0)",
            }}
            data-testid="sonmi-contribution"
          >
            <div className="flex gap-4">
              <ProvenanceMark author="sonmi" />
              <div
                className="text-stone-800"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 300,
                  fontSize: "1.08rem",
                  lineHeight: 1.6,
                  maxWidth: "520px",
                }}
              >
                <span data-testid="sonmi-clause-valid">
                  She has been pausing before schedule changes lately.
                </span>{" "}
                <span
                  data-testid="sonmi-clause-loose"
                  className="transition-all duration-1400"
                  style={{
                    opacity: wrongClauseGone ? 0 : wrongClauseLoosening ? 0.55 : 1,
                    display: "inline-block",
                    transform: wrongClauseGone
                      ? "translateY(-10px) scale(0.98)"
                      : "translateY(0) scale(1)",
                    color: wrongClauseGone
                      ? "rgba(120,113,108,0.4)"
                      : wrongClauseLoosening
                      ? "rgba(87,83,78,0.85)"
                      : undefined,
                  }}
                >
                  I wonder if the new rhythm is unsettling her.
                </span>
              </div>
            </div>
          </div>

          {/* Steward's turn — a real typed input, no border, no
              background. Placeholder ghost text shows only while
              empty. */}
          {step >= STEP.STEWARD_TURN && (
            <div
              className="absolute transition-all duration-1200"
              style={{
                top: survivorsDrawing ? "32%" : "38%",
                left: 0,
                right: "8%",
                opacity: 1,
              }}
              data-testid="steward-contribution"
            >
              <div className="flex gap-4 items-start">
                <ProvenanceMark author="steward" />
                <div style={{ maxWidth: "520px", width: "100%" }}>
                  {step === STEP.STEWARD_TURN ? (
                    <div
                      ref={editRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => setStewardText(e.currentTarget.textContent)}
                      onKeyDown={handleKeyDown}
                      data-testid="steward-input"
                      data-placeholder={GHOST}
                      className="steward-input outline-none text-stone-800 caret-amber-600"
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 300,
                        fontSize: "1.08rem",
                        lineHeight: 1.6,
                        minHeight: "1.6em",
                      }}
                    />
                  ) : (
                    <div
                      data-testid="steward-text"
                      className="text-stone-800"
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 300,
                        fontSize: "1.08rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {stewardText}
                    </div>
                  )}

                  {/* Small provenance hint appears next to the cursor
                      the first time the steward is invited. Not a
                      button — a whisper of context. */}
                  {step === STEP.STEWARD_TURN && (
                    <div className="text-[10px] italic text-stone-300 mt-2" data-testid="steward-hint">
                      your marker — press <span className="not-italic tracking-wider">↵</span> to leave your thought on the surface
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Revised understanding — belongs to both. No mark, no
              italic, no serif. Slightly larger and heavier than the
              contributions above; distinguished by weight, position,
              and the beat of empty space above it. */}
          {step >= STEP.SYNTH && step <= STEP.SYNTH && (
            <div
              className="absolute transition-all duration-1200"
              style={{
                top: "60%",
                left: 0,
                right: "8%",
                opacity: 1,
              }}
              data-testid="revised-understanding"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-3">
                what neither of us had, until now
              </div>
              <div
                className="text-stone-900"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 400,
                  fontSize: "1.28rem",
                  lineHeight: 1.5,
                  maxWidth: "560px",
                }}
              >
                Her pausing is care for the team — not uncertainty about the change.
              </div>

              {/* The confirm — contextual, next to the synthesis. Not
                  a bottom button. */}
              <button
                onClick={() => setStep(STEP.DISSOLVE)}
                data-testid="confirm"
                className="mt-6 text-[12px] italic text-stone-400 hover:text-stone-700 transition-colors"
              >
                yes
              </button>
            </div>
          )}
        </div>
      )}

      {/* Dissolution and remaining trace — the drop enters the
          shared surface, not Simone. Its arrival is a small
          surface-tension settle: scale overshoot into rest. No
          expanding ring. */}
      {step >= STEP.DISSOLVE && (
        <div
          className="absolute"
          style={{
            // Approximate centre of where the Vessel had been.
            left: "56%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
          data-testid="remaining-trace-wrapper"
        >
          {/* The aura — a brief soft glow that shows the surface
              receiving the drop, then contracts and disappears. */}
          <span
            className="absolute pointer-events-none rounded-full"
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 32,
              height: 32,
              background:
                "radial-gradient(circle, rgba(217,119,6,0.22) 0%, rgba(217,119,6,0.04) 55%, transparent 78%)",
              animation:
                "sonmi-drop-aura 900ms cubic-bezier(0.16, 0.68, 0.44, 1) forwards",
            }}
            data-testid="remaining-trace-aura"
          />
          {/* The drop itself — persists after dissolution. */}
          <span
            className="rounded-full block"
            style={{
              width: 5,
              height: 5,
              backgroundColor: "#d97706",
              opacity: 0.75,
              animation:
                "sonmi-drop-arrive 700ms cubic-bezier(0.16, 0.68, 0.44, 1) forwards",
            }}
            data-testid="remaining-trace-drop"
          />
        </div>
      )}

      <style>{`
        /* Ghost placeholder for the steward's contenteditable. */
        .steward-input:empty:before {
          content: attr(data-placeholder);
          color: rgba(120, 113, 108, 0.42);
          font-style: italic;
          pointer-events: none;
        }
        /* Drop arrival: brief overshoot into rest — no ring. */
        @keyframes sonmi-drop-arrive {
          0%   { transform: scale(0);   opacity: 0; }
          38%  { transform: scale(1.45); opacity: 0.9; }
          70%  { transform: scale(0.88); opacity: 0.72; }
          100% { transform: scale(1);   opacity: 0.75; }
        }
        /* Aura: appears, contracts, disappears. Does not expand
           outward. */
        @keyframes sonmi-drop-aura {
          0%   { transform: translate(-50%, -50%) scale(0);   opacity: 0; }
          35%  { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(0.55); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * The smallest possible provenance cue — a 1.5px vertical mark.
 * Amber for SONMI, stone for the steward. Same shape; only color
 * distinguishes. Same voice above; only the mark distinguishes.
 */
function ProvenanceMark({ author }) {
  const color = author === "sonmi" ? "#d97706" : "#a8a29e";
  return (
    <span
      aria-hidden="true"
      data-testid={`provenance-${author}`}
      className="inline-block flex-shrink-0"
      style={{
        width: 1.5,
        height: 18,
        backgroundColor: color,
        marginTop: 6,
        opacity: author === "sonmi" ? 0.85 : 0.7,
      }}
    />
  );
}
